<div class="send-schedule-dialog">

  <!-- Header -->
  <div class="dialog-header">
    <div class="header-left">
      <i class="pi pi-send header-icon"></i>
      <div>
        <h3 class="dialog-title">Send Schedule to Crew</h3>
        <p class="dialog-subtitle">Review, preview, and send today's schedule via SMS and Email</p>
      </div>
    </div>
    <p-button
      icon="pi pi-times"
      [text]="true"
      severity="secondary"
      ariaLabel="Close send schedule dialog"
      (onClick)="close()"
    />
  </div>

  <p-divider styleClass="m-0" />

  <!-- Main content: left panel + right panel -->
  <div class="dialog-body">

    <!-- LEFT PANEL: Routes + Crew -->
    <div class="left-panel">
      <div class="panel-actions">
        <p-button
          label="Select Changed / Unsent"
          icon="pi pi-refresh"
          size="small"
          [text]="true"
          (onClick)="selectAllChanged()"
        />
        <p-button
          label="Deselect All"
          icon="pi pi-ban"
          size="small"
          [text]="true"
          severity="secondary"
          (onClick)="deselectAll()"
        />
      </div>

      <div class="route-list">
        <ng-container *ngFor="let route of routeTree; trackBy: trackByRouteId">
          <div class="route-group">

            <!-- Route header -->
            <div class="route-header">
              <i class="pi pi-truck route-icon"></i>
              <span class="route-name">{{ route.routeName }}</span>
            </div>

            <!-- Crew list -->
            <div class="crew-list">
              <div
                *ngFor="let crewItem of route.crew; trackBy: trackByCrewId"
                class="crew-item"
                [class.crew-item--selected-preview]="selectedPreviewAssetId === route.assetId && selectedPreviewUserId === crewItem.crewMember.user.id"
                [class.crew-item--no-contact]="!crewItem.canSend"
                (click)="selectCrewForPreview(route.assetId, crewItem.crewMember.user.id)"
              >
                <div class="crew-item-top">
                  <p-checkbox
                    [binary]="true"
                    [disabled]="!crewItem.canSend"
                    [ngModel]="crewItem.isSelected"
                    (ngModelChange)="toggleCrew(crewItem.crewMember.user.id, route.assetId, $event)"
                    (click)="$event.stopPropagation()"
                    [inputId]="'crew-checkbox-' + crewItem.crewMember.user.id + '-' + route.assetId"
                    [ariaLabel]="'Select ' + crewItem.crewMember.name + ' for schedule send'"
                  />
                  <!-- D-9 (Maria): the name is NOT the checkbox trigger —
                       row click previews the schedule; selection happens on
                       the checkbox only. -->
                  <span class="crew-name">{{ crewItem.crewMember.name }}</span>
                  <p-tag
                    [value]="getStatusLabel(crewItem.sendStatus)"
                    [severity]="getStatusSeverity(crewItem.sendStatus)"
                    [icon]="getStatusIcon(crewItem.sendStatus)"
                    styleClass="status-tag"
                  />
                </div>
                <!-- Contact info row -->
                <div class="crew-contact-row">
                  <span
                    class="contact-chip"
                    [class.contact-chip--valid]="crewItem.hasValidEmail"
                    [class.contact-chip--invalid]="!crewItem.hasValidEmail"
                    [pTooltip]="crewItem.hasValidEmail ? ('Email: ' + crewItem.email) : 'No valid email — email will not be sent'"
                  >
                    <i class="pi pi-envelope"></i>
                    <span class="contact-value">{{ crewItem.hasValidEmail ? crewItem.email : 'No email' }}</span>
                  </span>
                  <span
                    class="contact-chip"
                    [class.contact-chip--valid]="crewItem.hasValidPhone"
                    [class.contact-chip--invalid]="!crewItem.hasValidPhone"
                    [pTooltip]="crewItem.hasValidPhone ? ('SMS: ' + crewItem.phone) : 'No valid phone — SMS will not be sent'"
                  >
                    <i class="pi pi-mobile"></i>
                    <span class="contact-value">{{ crewItem.hasValidPhone ? crewItem.phone : 'No phone' }}</span>
                  </span>
                  <span
                    *ngIf="!crewItem.canSend"
                    class="no-contact-warning"
                    pTooltip="No email or phone — cannot send schedule"
                  >
                    <i class="pi pi-ban"></i> Cannot send
                  </span>
                </div>
              </div>

              <!-- Missing crew placeholder (D-12: single label) -->
              <div *ngIf="route.hasMissingCrew" class="crew-item crew-item--missing">
                <i class="pi pi-user-plus"></i>
                <span class="crew-name missing">Crew needed</span>
              </div>
            </div>

            <!-- Thread history for this route -->
            <app-dispatch-route-threads
              [assetId]="route.assetId"
              [threads]="route.threads"
            />

            <!-- Route custom note (D-12: hidden when the route has no crew) -->
            <div class="route-note" *ngIf="route.crew?.length">
              <label
                [for]="'route-note-' + route.assetId"
                class="note-label"
              >Route note (included for all crew on this route):</label>
              <textarea
                pInputTextarea
                [id]="'route-note-' + route.assetId"
                [attr.aria-label]="'Route note for ' + route.routeName"
                [rows]="2"
                [autoResize]="true"
                [ngModel]="assetNotes[route.assetId] || ''"
                (ngModelChange)="onAssetNoteChange(route.assetId, $event)"
                placeholder="Optional note for this route..."
                class="note-input"
              ></textarea>
            </div>

          </div>
        </ng-container>

        <!-- Empty state -->
        <div *ngIf="!routeTree.length" class="empty-state">
          <i class="pi pi-info-circle"></i>
          <p>No routes with assigned crew found for today. Assign crew to events first.</p>
        </div>

      </div>
    </div>

    <!-- RIGHT PANEL: Preview -->
    <div class="right-panel">
      <div *ngIf="!selectedPreviewKey" class="preview-empty">
        <i class="pi pi-eye preview-empty-icon"></i>
        <p>Click a crew member on the left to preview their schedule.</p>
      </div>

      <ng-container *ngIf="selectedPreviewKey">
        <div class="preview-header">
          <h5 class="preview-title">Schedule Preview</h5>
          <p-tabView [(activeIndex)]="activePreviewTab" styleClass="preview-tabs">
            <p-tabPanel header="Email">
              <ng-container *ngTemplateOutlet="emailPreview"></ng-container>
            </p-tabPanel>
            <p-tabPanel header="SMS">
              <ng-container *ngTemplateOutlet="smsPreview"></ng-container>
            </p-tabPanel>
          </p-tabView>
        </div>

        <!-- Crew-specific note -->
        <div class="crew-note-section" *ngIf="selectedPreviewUserId">
          <label
            for="crew-note-selected"
            class="note-label"
          >Personal note for this crew member:</label>
          <textarea
            pInputTextarea
            id="crew-note-selected"
            [attr.aria-label]="'Personal note for selected crew member'"
            [rows]="2"
            [autoResize]="true"
            [ngModel]="crewNotes[selectedPreviewUserId] || ''"
            (ngModelChange)="onCrewNoteChange(selectedPreviewUserId, $event)"
            placeholder="Optional personal note..."
            class="note-input"
          ></textarea>
        </div>
      </ng-container>
    </div>

  </div>

  <!-- Footer -->
  <p-divider styleClass="m-0" />

  <!-- Warning: selected crew with no contact info -->
  <div *ngIf="selectedWithNoContact.length" class="footer-warning">
    <i class="pi pi-exclamation-triangle"></i>
    <span>
      {{ selectedWithNoContact.length }} selected crew member{{ selectedWithNoContact.length !== 1 ? 's have' : ' has' }}
      no email or phone and will be skipped:
      <strong>{{ selectedWithNoContactNames }}</strong>
    </span>
  </div>

  <div class="dialog-footer">
    <div class="footer-left">
      <span class="selected-count" *ngIf="(selectedCount$ | async) as count">
        {{ count }} crew member{{ count !== 1 ? 's' : '' }} selected
      </span>
    </div>
    <div class="footer-actions">
      <p-button
        label="Cancel"
        severity="secondary"
        [outlined]="true"
        (onClick)="close()"
      />
      <p-button
        [label]="'Send Schedule (' + ((selectedCount$ | async) || 0) + ')'"
        icon="pi pi-send"
        [loading]="isSubmitting$ | async"
        [disabled]="(selectedCount$ | async) === 0 || (selectedCount$ | async) === null"
        (onClick)="send()"
      />
    </div>
  </div>

</div>

<!-- Email Preview Template -->
<ng-template #emailPreview>
  <ng-container *ngIf="currentPreview as preview">
    <div *ngIf="preview.loading" class="preview-loading">
      <p-progressSpinner [style]="{ width: '40px', height: '40px' }" />
      <span>Generating preview...</span>
    </div>
    <div *ngIf="!preview.loading && preview.emailHtml" class="email-preview">
      <div class="email-subject">
        <strong>Subject:</strong> {{ preview.emailSubject }}
      </div>
      <!-- iframe preserves all CSS — Angular's innerHTML strips style tags and class-based styles -->
      <iframe
        class="email-frame"
        [srcdoc]="getEmailPreviewSrcdoc(preview.emailHtml)"
        sandbox="allow-same-origin"
        scrolling="no"
        title="Email preview"
        (load)="onEmailFrameLoad($event)"
      ></iframe>
    </div>
    <div *ngIf="!preview.loading && !preview.emailHtml" class="preview-error">
      <i class="pi pi-exclamation-circle"></i>
      <span>Could not generate preview. Check that the asset and crew member have events assigned.</span>
    </div>
  </ng-container>
</ng-template>

<!-- SMS Preview Template -->
<ng-template #smsPreview>
  <ng-container *ngIf="currentPreview as preview">
    <div *ngIf="preview.loading" class="preview-loading">
      <p-progressSpinner [style]="{ width: '40px', height: '40px' }" />
      <span>Generating preview...</span>
    </div>
    <div *ngIf="!preview.loading && preview.smsBody" class="sms-preview">
      <div class="sms-bubble">
        <pre class="sms-text">{{ preview.smsBody }}</pre>
      </div>
    </div>
    <div *ngIf="!preview.loading && !preview.smsBody" class="preview-error">
      <i class="pi pi-exclamation-circle"></i>
      <span>Could not generate SMS preview.</span>
    </div>
  </ng-container>
</ng-template>
